import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, Link as LinkIcon, X, Film, Image as ImageIcon } from 'lucide-react';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGE_MB = 30;
const MAX_VIDEO_MB = 100;
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024;
const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;

const isVideoUrl = (url, mediaType) => {
  if (mediaType === 'video') return true;
  if (!url) return false;
  return /\.(mp4|mov|webm)(\?.*)?$/i.test(url);
};

/**
 * Reusable media input — supports image + video upload, plus URL paste fallback.
 * Props:
 *  - value: current url (string)
 *  - onChange(url, mediaType): called with new url + 'image'|'video'
 *  - mediaType: current media type (for re-render of preview)
 *  - accept: 'image' (default) | 'video' | 'both'
 */
const ImageUploadField = ({
  value,
  onChange,
  mediaType,
  accept = 'both',
  label = 'Media',
  testIdPrefix = 'media'
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const acceptAttr =
    accept === 'image' ? 'image/*' :
    accept === 'video' ? 'video/mp4,video/quicktime' :
    'image/*,video/mp4,video/quicktime';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isImage && !isVideo) {
      toast.error('Please choose an image or video file');
      return;
    }
    if (accept === 'image' && !isImage) {
      toast.error('Only images allowed here');
      return;
    }
    if (accept === 'video' && !isVideo) {
      toast.error('Only videos allowed here');
      return;
    }
    if (isImage && file.size > MAX_IMAGE_BYTES) {
      toast.error(`Image must be under ${MAX_IMAGE_MB}MB`);
      return;
    }
    if (isVideo && file.size > MAX_VIDEO_BYTES) {
      toast.error(`Video must be under ${MAX_VIDEO_MB}MB`);
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 5 * 60 * 1000 // 5 min for large videos
      });
      onChange(res.data.url, res.data.kind);
      toast.success(`${res.data.kind === 'video' ? 'Video' : 'Image'} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const showAsVideo = isVideoUrl(value, mediaType);
  const limitText =
    accept === 'image' ? `JPG, PNG, WebP, or GIF. Max ${MAX_IMAGE_MB}MB.` :
    accept === 'video' ? `MP4 or MOV. Max ${MAX_VIDEO_MB}MB.` :
    `Images up to ${MAX_IMAGE_MB}MB · Videos (MP4, MOV) up to ${MAX_VIDEO_MB}MB.`;

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-field`}>
      {label && <label className="block text-[#A19D98] text-sm">{label}</label>}

      {/* Preview */}
      {value && (
        <div className="relative inline-block">
          {showAsVideo ? (
            <video
              src={value}
              className="w-48 h-32 object-cover border border-[#2A2A2A] bg-[#0A0A0A]"
              controls
              muted
              playsInline
              data-testid={`${testIdPrefix}-preview-video`}
            />
          ) : (
            <img
              src={value}
              alt="preview"
              className="w-32 h-32 object-cover border border-[#2A2A2A]"
              onError={(e) => { e.target.style.display = 'none'; }}
              data-testid={`${testIdPrefix}-preview-image`}
            />
          )}
          <button
            type="button"
            onClick={() => onChange('', null)}
            data-testid={`${testIdPrefix}-clear-btn`}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#0A0A0A] border border-[#2A2A2A] text-[#A19D98] hover:text-[#E8651A] flex items-center justify-center"
            title="Clear"
          >
            <X size={12} />
          </button>
          <div className="absolute bottom-1 left-1 bg-black/70 text-[#F2EDE6] text-[10px] px-1.5 py-0.5 flex items-center gap-1 font-['Oswald'] tracking-wider">
            {showAsVideo ? <><Film size={10} /> VIDEO</> : <><ImageIcon size={10} /> IMAGE</>}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="file"
          accept={acceptAttr}
          ref={fileRef}
          onChange={handleFile}
          data-testid={`${testIdPrefix}-file-input`}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-upload-btn`}
          className="btn-secondary flex items-center justify-center gap-2 text-sm disabled:opacity-50"
        >
          {uploading ? (
            <><Loader2 size={14} className="animate-spin" /> Uploading {progress}%</>
          ) : (
            <><Upload size={14} /> Upload File</>
          )}
        </button>

        <div className="flex-1 flex items-center gap-2">
          <LinkIcon size={14} className="text-[#A19D98]" />
          <Input
            type="url"
            value={value || ''}
            onChange={(e) => {
              const url = e.target.value;
              onChange(url, isVideoUrl(url) ? 'video' : 'image');
            }}
            placeholder="...or paste URL"
            data-testid={`${testIdPrefix}-url-input`}
            className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6] text-sm"
          />
        </div>
      </div>
      <p className="text-[#A19D98] text-xs">{limitText}</p>
    </div>
  );
};

export default ImageUploadField;
